import { z } from 'zod';

/**
 * Reglas de una política de jornada.
 *
 * Una política es CONFIGURACIÓN TÉCNICA de cómo WulfTime registra y computa. Que el sistema permita
 * seleccionar una regla no implica que la empresa pueda aplicarla legalmente: depende del Estatuto de los
 * Trabajadores, del convenio colectivo y de los acuerdos aplicables. Ver `policyWarnings()`.
 *
 * Las políticas se guardan versionadas (work_policy_versions). Una jornada siempre se computa con la versión
 * que estaba vigente ese día, nunca con la actual.
 */

export const ComputationModeSchema = z.enum(['REAL', 'SCHEDULED', 'HYBRID']);
export type ComputationMode = z.infer<typeof ComputationModeSchema>;

export const GpsRequirementSchema = z.enum(['REQUIRED', 'OPTIONAL', 'DISABLED']);
export type GpsRequirement = z.infer<typeof GpsRequirementSchema>;

export const ZoneActionSchema = z.enum(['ALLOW', 'ALLOW_WITH_REASON', 'BLOCK']);
export type ZoneAction = z.infer<typeof ZoneActionSchema>;

export const ClockMethodSchema = z.enum(['MOBILE', 'WEB', 'KIOSK', 'API']);
export type ClockMethod = z.infer<typeof ClockMethodSchema>;

export const AnomalyCodeSchema = z.enum([
  'MISSING_CLOCK_IN',
  'MISSING_CLOCK_OUT',
  'MISSING_BREAK',
  'LATE_ARRIVAL',
  'EARLY_DEPARTURE',
  'BREAK_EXCEEDED',
  'INCOMPLETE_DAY',
  'OUTSIDE_ZONE',
  'DUPLICATE_EVENT',
  'INCONSISTENT_EVENTS',
  'UNSCHEDULED_WORK',
  'WORK_ON_HOLIDAY',
  'WORK_DURING_ABSENCE',
  'GPS_NOT_VALIDATED',
  'OFFLINE_REVIEW',
  'EXCESS_HOURS',
  'AUTO_START_NO_PERSONAL_CLOCK',
  'LONG_SHIFT',
  'AUTO_CLOCK_OUT',
]);
export type AnomalyCode = z.infer<typeof AnomalyCodeSchema>;

const anomalyConfig = z.object({
  /** Crear una incidencia automáticamente cuando se detecte. */
  incident: z.boolean(),
  /** Notificar al responsable. */
  notifyManager: z.boolean(),
});
export type AnomalyConfig = z.infer<typeof anomalyConfig>;

export const DEFAULT_ANOMALY_CONFIG: Record<AnomalyCode, AnomalyConfig> = {
  MISSING_CLOCK_IN: { incident: true, notifyManager: true },
  MISSING_CLOCK_OUT: { incident: true, notifyManager: true },
  MISSING_BREAK: { incident: true, notifyManager: false },
  LATE_ARRIVAL: { incident: false, notifyManager: false },
  EARLY_DEPARTURE: { incident: true, notifyManager: true },
  BREAK_EXCEEDED: { incident: false, notifyManager: false },
  // Informativa: la diferencia se ve en la jornada y en los informes. Como incidencia generaba mucho ruido (p. ej. cada pausa no planificada).
  INCOMPLETE_DAY: { incident: false, notifyManager: false },
  OUTSIDE_ZONE: { incident: true, notifyManager: true },
  DUPLICATE_EVENT: { incident: false, notifyManager: false },
  INCONSISTENT_EVENTS: { incident: true, notifyManager: true },
  UNSCHEDULED_WORK: { incident: true, notifyManager: false },
  WORK_ON_HOLIDAY: { incident: true, notifyManager: true },
  WORK_DURING_ABSENCE: { incident: true, notifyManager: true },
  GPS_NOT_VALIDATED: { incident: true, notifyManager: false },
  OFFLINE_REVIEW: { incident: true, notifyManager: false },
  EXCESS_HOURS: { incident: false, notifyManager: false },
  AUTO_START_NO_PERSONAL_CLOCK: { incident: true, notifyManager: true },
  LONG_SHIFT: { incident: true, notifyManager: true },
  AUTO_CLOCK_OUT: { incident: false, notifyManager: false },
};

const minutes = (max: number) => z.number().int().min(0).max(max);

export const PolicyRulesSchema = z.object({
  /** Métodos de fichaje permitidos. */
  methods: z
    .object({
      mobile: z.boolean().default(true),
      web: z.boolean().default(true),
      kiosk: z.boolean().default(true),
      api: z.boolean().default(false),
    })
    .default({}),

  clockIn: z
    .object({
      /** Cuánto antes del inicio planificado se permite fichar la entrada. */
      earlyWindowMinutes: minutes(720).default(15),
      /** Margen tras el inicio planificado antes de considerar "llegada tarde". */
      lateToleranceMinutes: minutes(240).default(5),
      /** Cómo se computa la entrada: REAL, SCHEDULED (siempre la planificada) o HYBRID (antes → planificada; después → real). */
      computation: ComputationModeSchema.default('HYBRID'),
      /** Qué hacer si el trabajador ficha en un día sin jornada planificada (descanso, festivo, sin horario...). */
      withoutSchedule: z.enum(['ALLOW', 'ALLOW_WITH_INCIDENT', 'BLOCK']).default('ALLOW_WITH_INCIDENT'),
    })
    .default({}),

  clockOut: z
    .object({
      /** Margen antes de la salida planificada que no se considera salida anticipada. */
      earlyToleranceMinutes: minutes(240).default(5),
      /** Si es false, la salida anticipada se registra igualmente (nunca se impide registrar la realidad) pero genera incidencia que requiere aprobación. */
      allowEarly: z.boolean().default(true),
      requireReasonIfEarly: z.boolean().default(true),
      requireApprovalIfEarly: z.boolean().default(false),
      allowLate: z.boolean().default(true),
      /** Cómputo de la salida posterior a la planificada: REAL (computa la hora real) o SCHEDULED (computa hasta la hora planificada; el exceso queda registrado aparte). */
      lateComputation: z.enum(['REAL', 'SCHEDULED']).default('REAL'),
      lateToleranceMinutes: minutes(240).default(5),
      /** Duración máxima razonable de una jornada abierta antes de marcarla como posible salida olvidada. */
      maxShiftHours: z.number().min(1).max(24).default(14), // las versiones nuevas exigen ≥ 6 (ver policyErrors)
    })
    .default({}),

  excess: z
    .object({
      /** TRACK_ONLY: muestra "exceso respecto a jornada prevista" sin calificarlo. OVERTIME_POLICY: la empresa ha configurado explícitamente horas extraordinarias. */
      mode: z.enum(['TRACK_ONLY', 'OVERTIME_POLICY']).default('TRACK_ONLY'),
      overtimeRequiresAuthorization: z.boolean().default(true),
      /** Exceso mínimo (min) para que se señale. */
      minExcessMinutes: minutes(240).default(15),
    })
    .default({}),

  breaks: z
    .object({
      /** Si las pausas no planificadas cuentan como tiempo de trabajo por defecto. */
      defaultComputable: z.boolean().default(false),
      allowUnplanned: z.boolean().default(true),
      maxBreaksPerDay: z.number().int().min(0).max(20).default(5),
      exceededAction: z.enum(['NONE', 'NOTIFY', 'INCIDENT']).default('INCIDENT'),
      /** Pausa obligatoria planificada no registrada: no hacer nada, descontar la duración planificada (marcado como tal) o crear incidencia. */
      missingAction: z.enum(['NONE', 'DEDUCT_PLANNED', 'INCIDENT']).default('INCIDENT'),
    })
    .default({}),

  gps: z
    .object({
      clockIn: GpsRequirementSchema.default('DISABLED'),
      clockOut: GpsRequirementSchema.default('DISABLED'),
      breaks: GpsRequirementSchema.default('DISABLED'),
      outsideZone: ZoneActionSchema.default('ALLOW_WITH_REASON'),
      /** Precisión máxima aceptable (m). Peor precisión → resultado LOW_ACCURACY. */
      maxAccuracyMeters: z.number().int().min(5).max(5000).default(150),
      lowAccuracy: ZoneActionSchema.default('ALLOW'),
      /** GPS obligatorio pero no disponible (sin permiso, apagado, timeout): bloquear o permitir marcándolo como no validado. */
      unavailable: z.enum(['BLOCK', 'ALLOW_UNVALIDATED']).default('BLOCK'),
    })
    .default({}),

  autoStart: z
    .object({
      /** Crea un "inicio previsto/automático" a la hora planificada. Nunca es un fichaje personal del trabajador. */
      enabled: z.boolean().default(false),
      /** Minutos tras el inicio sin fichaje personal para aplicar `whenNoPersonalClock`. */
      graceMinutes: minutes(720).default(30),
      /** INCIDENT_ONLY: solo incidencia. COMPUTE_FROM_PLANNED: computa desde el inicio automático (marcado como no personal). PENDING: queda pendiente sin computar. */
      whenNoPersonalClock: z.enum(['INCIDENT_ONLY', 'COMPUTE_FROM_PLANNED', 'PENDING']).default('INCIDENT_ONLY'),
    })
    .default({}),

  autoClockOut: z
    .object({
      /** Si el trabajador olvida fichar la salida, el sistema registra una «salida automática» a la hora prevista de fin. Nunca es un fichaje personal. */
      enabled: z.boolean().default(false),
      /** Minutos tras la salida prevista sin fichaje para registrarla automáticamente (da tiempo a fichar tarde). */
      graceMinutes: minutes(720).default(60),
    })
    .default({}),

  duplicates: z
    .object({
      /** Mismo tipo de evento repetido en menos de N segundos → se considera duplicado y no se registra dos veces. */
      minSecondsBetweenSameEvent: z.number().int().min(0).max(3600).default(60),
    })
    .default({}),

  offline: z
    .object({
      allowed: z.boolean().default(true),
      /** Eventos offline más antiguos que esto al sincronizar → revisión obligatoria. */
      maxOfflineHours: z.number().min(0).max(720).default(72),
      review: z.enum(['NEVER', 'ALWAYS', 'IF_SUSPICIOUS']).default('IF_SUSPICIOUS'),
      /** Desfase máximo tolerado entre reloj del dispositivo y servidor. */
      maxClockSkewSeconds: z.number().int().min(0).max(86400).default(120),
    })
    .default({}),

  /**
   * Salidas posteriores a la hora prevista: la empresa las revisa y las clasifica (olvido con la hora real de fin,
   * motivo personal o trabajo). La hora real nunca cambia; el trabajador ve la clasificación y puede discrepar.
   */
  lateExits: z
    .object({
      reviewEnabled: z.boolean().default(false),
      /** Minutos de margen: las salidas dentro de este margen no se revisan. */
      thresholdMinutes: minutes(240).default(10),
    })
    .default({}),

  /** Bolsa de horas: el tiempo trabajado de más (clasificado como trabajo) se compensa con descanso. */
  hoursBank: z
    .object({
      enabled: z.boolean().default(false),
      /** Las jornadas con menos horas de las previstas restan del saldo. */
      countDeficit: z.boolean().default(true),
    })
    .default({}),

  corrections: z
    .object({
      allowWorkerRequests: z.boolean().default(true),
      evidenceAllowed: z.boolean().default(true),
      evidenceRequired: z.boolean().default(false),
      maxDaysBack: z.number().int().min(0).max(365).default(31),
      /** Aprobación automática de olvidos de entrada/salida dentro de unos límites (lo demás lo revisa un responsable). */
      autoApprove: z
        .object({
          enabled: z.boolean().default(false),
          maxMinutesFromPlanned: z.number().int().min(0).max(240).default(30),
          maxPerMonth: z.number().int().min(1).max(31).default(3),
          maxDaysBack: z.number().int().min(0).max(31).default(3),
        })
        .default({}),
    })
    .default({}),

  presenceVerification: z
    .object({
      /** Verificación puntual de presencia. Desactivada por defecto. Requiere justificación y nunca es seguimiento continuo. */
      enabled: z.boolean().default(false),
      maxPerEmployeePerDay: z.number().int().min(0).max(5).default(1),
      responseMinutes: z.number().int().min(1).max(120).default(15),
    })
    .default({}),

  anomalies: z
    .record(AnomalyCodeSchema, anomalyConfig)
    .default({})
    .transform((v) => ({ ...DEFAULT_ANOMALY_CONFIG, ...v }) as Record<AnomalyCode, AnomalyConfig>),

  /** Notas internas: base legal/convenio que justifica la configuración. Texto libre de la empresa. */
  notes: z.string().max(4000).default(''),
});

export type PolicyRules = z.infer<typeof PolicyRulesSchema>;
export type PolicyRulesInput = z.input<typeof PolicyRulesSchema>;

export function parsePolicyRules(input: unknown): PolicyRules {
  return PolicyRulesSchema.parse(input ?? {});
}

export const DEFAULT_POLICY_RULES: PolicyRules = parsePolicyRules({});

/** Reglas de notificación a nivel de empresa (las preferencias de cada usuario se aplican encima). */
export const NotificationRulesSchema = z.object({
  clockInReminderBeforeMinutes: minutes(240).default(10),
  missingClockInAfterMinutes: minutes(720).default(15),
  breakStartReminder: z.boolean().default(true),
  breakEndReminderBeforeMinutes: minutes(120).default(5),
  missingBreakEndAfterMinutes: minutes(240).default(15),
  clockOutReminderBeforeMinutes: minutes(240).default(10),
  missingClockOutAfterMinutes: minutes(720).default(30),
  channels: z
    .object({
      push: z.boolean().default(true),
      email: z.boolean().default(true),
      inApp: z.boolean().default(true),
    })
    .default({}),
});
export type NotificationRules = z.infer<typeof NotificationRulesSchema>;
export const DEFAULT_NOTIFICATION_RULES: NotificationRules = NotificationRulesSchema.parse({});
